import React from 'react';

/*
 * A locale group's mark -- a flag, else a code badge -- beside its name: decoration, aria-hidden,
 * the name in its own language being the label. Both sit in one fixed slot, so every row of a
 * list lines up whichever it shows. The nav's language button draws the mark alone, its name
 * kept for the accessible name only.
 *
 * The flag is an emoji built from the region's two regional indicator symbols, so no image, no
 * asset, and no CSP change. The region is the tag's own (pt-BR, en-US), else CLDR's likely
 * subtags (fr is fr-Latn-FR, ja is ja-Jpan-JP, zh-Hant is zh-Hant-TW).
 *
 * THE BADGE is the key's language and region in capitals (AR, ES-419), for a group with no
 * single country to fly: Arabic, whose likely subtags would name Egypt for a language of some
 * twenty countries (NO_FLAG), and a numeric UN M.49 area such as es-419, Latin America. Not a
 * globe: the menu's button falls back to one, and a globe in a row reads as "every language".
 *
 * Windows draws no flag emoji -- Chrome and Edge there show the two letters, FR, unboxed -- so
 * on Windows every group shows its badge. Firefox carries its own emoji font and draws flags
 * everywhere.
 */

const NO_FLAG = new Set( [ 'ar' ] );
const INDICATOR_A = 0x1F1E6;

/** Whether this browser draws a flag emoji as a flag rather than as two letters. */
export function esFlagsDrawn( pUserAgent = typeof navigator === 'undefined' ? '' : navigator.userAgent ) {
    const aAgent = String( pUserAgent ?? '' );
    return !/Windows/.test( aAgent ) || /Firefox\//.test( aAgent );
}

/**
 * pKey's mark: { flag } as an emoji, or { badge } as its language and region in capitals when it
 * has no single country or pDrawn says this browser draws no flags. null for a key not a tag.
 */
export function esLocaleMark( pKey, pDrawn = esFlagsDrawn() ) {
    let aLocale;
    try {
        aLocale = new Intl.Locale( String( pKey ?? '' ) );
    }
    catch {
        return null;
    }
    const aBadge = { badge: ( aLocale.language + ( aLocale.region ? '-' + aLocale.region : '' ) ).toUpperCase() };
    if ( !pDrawn || ( NO_FLAG.has( aLocale.language ) && !aLocale.region ) ) return aBadge;

    const aRegion = aLocale.region ?? aLocale.maximize().region;
    if ( !aRegion || !/^[A-Za-z]{2}$/.test( aRegion ) ) return aBadge;
    return { flag: String.fromCodePoint( ...aRegion.toUpperCase().split( '' ).map( ( pLetter ) => INDICATOR_A + pLetter.charCodeAt( 0 ) - 65 ) ) };
}

/** The group's mark in its fixed slot, then its name in its own language. */
export function EsLocaleName( { group, display = 'inline-flex', className = '', nameId } ) {
    const aMark = esLocaleMark( group?.key );
    return (
        <span className={ display + ' items-center gap-1.5 ' + className }>
            { aMark && (
                <span aria-hidden="true" data-mark={ aMark.flag ? 'flag' : 'badge' } className="inline-flex w-8 shrink-0 justify-center">
                    { aMark.flag ? <span className="text-xl leading-none">{ aMark.flag }</span> : <span className="rounded-sm border border-current px-0.5 text-[0.6rem] font-semibold leading-tight opacity-80">{ aMark.badge }</span> }
                </span>
            ) }
            <span id={ nameId } lang={ group?.key }>{ group?.nativeName ?? group?.key }</span>
        </span>
    );
}
